/**
 * CKYC Report Download Helper
 * Downloads the S3 PDF report if ckyc_report_url is present,
 * or generates and downloads a formatted CKYC Report document.
 */

export const downloadCkycReport = async (reportData) => {
    if (!reportData) return;

    const pd = reportData.personal_details || {};
    const idDetails = reportData.identity_details?.identity || [];
    const imageDetails = reportData.image_details?.image || [];
    const photograph = imageDetails.find(img => img.image_code === "Photograph")?.image_url;
    const ckycNo = pd.ckyc_no || pd.ckyc_reference_id || "Report";
    const pan = pd.pan || "";
    const fullName = pd.full_name || "Customer";

    // 1. If remote S3 URL is available, attempt direct file download
    const reportUrl = reportData.ckyc_report_url || reportData.report_url;
    if (reportUrl && typeof reportUrl === 'string' && reportUrl.startsWith('http')) {
        try {
            const res = await fetch(reportUrl);
            if (res.ok) {
                const blob = await res.blob();
                const blobUrl = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = blobUrl;
                a.download = `CKYC_Report_${ckycNo}_${pan || ''}.pdf`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(blobUrl);
                return;
            }
        } catch (err) {
            console.warn("Direct S3 download failed, falling back to window open or printable report", err);
            window.open(reportUrl, '_blank');
            return;
        }
    }

    // 2. Generate formatted printable/downloadable CKYC report
    const getIdentity = (type) => idDetails.find(id => id.ident_type === type)?.ident_num || '-';

    const htmlContent = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <title>CKYC Report - ${fullName} (${ckycNo})</title>
    <style>
        @page {
            size: A4;
            margin: 12mm 15mm;
        }
        * {
            box-sizing: border-box;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
        }
        body {
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
            color: #2c3e50;
            background: #fff;
            margin: 0;
            padding: 20px;
            font-size: 13px;
            line-height: 1.5;
        }
        .header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            border-bottom: 2px solid #bb2124;
            padding-bottom: 12px;
            margin-bottom: 20px;
        }
        .header-title h1 {
            font-size: 20px;
            margin: 0 0 4px 0;
            color: #bb2124;
        }
        .header-title p {
            margin: 0;
            color: #666;
            font-size: 12px;
        }
        .header-badge {
            background-color: #e8f5e9;
            color: #2e7d32;
            border: 1px solid #a5d6a7;
            padding: 6px 14px;
            border-radius: 4px;
            font-weight: 600;
            font-size: 13px;
        }
        .grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 16px;
            margin-bottom: 16px;
        }
        .card {
            border: 1px solid #e0e0e0;
            border-radius: 6px;
            padding: 14px;
            background: #fafafa;
            page-break-inside: avoid;
        }
        .card-title {
            font-size: 14px;
            font-weight: 700;
            color: #1a237e;
            margin: 0 0 10px 0;
            padding-bottom: 6px;
            border-bottom: 1px solid #e0e0e0;
        }
        .sub-header {
            font-size: 13px;
            font-weight: 600;
            color: #bb2124;
            margin: 10px 0 6px 0;
        }
        .detail-row {
            display: flex;
            justify-content: space-between;
            padding: 4px 0;
            border-bottom: 1px dashed #eee;
        }
        .detail-row:last-child {
            border-bottom: none;
        }
        .label {
            font-weight: 500;
            color: #616161;
            width: 45%;
        }
        .value {
            font-weight: 600;
            color: #212121;
            width: 55%;
            word-break: break-word;
        }
        .photo-card {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            text-align: center;
        }
        .photo-img {
            max-height: 150px;
            max-width: 150px;
            border-radius: 4px;
            border: 1px solid #ccc;
            object-fit: cover;
            margin-bottom: 8px;
        }
        .photo-placeholder {
            width: 130px;
            height: 150px;
            background: #e0e0e0;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 4px;
            color: #757575;
            margin-bottom: 8px;
        }
        table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 6px;
            font-size: 12px;
        }
        table th, table td {
            border: 1px solid #e0e0e0;
            padding: 6px 8px;
            text-align: left;
        }
        table th {
            background-color: #eceff1;
            font-weight: 600;
            color: #37474f;
        }
        .footer {
            margin-top: 20px;
            padding-top: 10px;
            border-top: 1px solid #ddd;
            display: flex;
            justify-content: space-between;
            font-size: 11px;
            color: #9e9e9e;
        }
        .no-print {
            margin-bottom: 16px;
            display: flex;
            justify-content: flex-end;
            gap: 10px;
        }
        .btn {
            background: #bb2124;
            color: #fff;
            border: none;
            padding: 8px 16px;
            border-radius: 4px;
            font-weight: 600;
            cursor: pointer;
            font-size: 13px;
        }
        @media print {
            .no-print {
                display: none !important;
            }
            body {
                padding: 0;
            }
        }
    </style>
</head>
<body>
    <div class="no-print">
        <button class="btn" onclick="window.print()">Print / Save as PDF</button>
    </div>

    <div class="header">
        <div class="header-title">
            <h1>Central KYC Records Registry (CKYC) Report</h1>
            <p>Report Reference: ${pd.ckyc_reference_id || '-'} | Generated On: ${new Date().toLocaleString()}</p>
        </div>
        <div class="header-badge">
            CKYC Status: Active
        </div>
    </div>

    <div class="grid">
        <!-- Personal Information -->
        <div class="card">
            <h3 class="card-title">Personal Information</h3>
            <div class="detail-row"><span class="label">Full Name</span><span class="value">${pd.full_name || '-'}</span></div>
            <div class="detail-row"><span class="label">Father's Name</span><span class="value">${pd.father_fullname || '-'}</span></div>
            <div class="detail-row"><span class="label">Mother's Name</span><span class="value">${pd.mother_fullname || '-'}</span></div>
            <div class="detail-row"><span class="label">Date of Birth</span><span class="value">${pd.dob || '-'}</span></div>
            <div class="detail-row"><span class="label">Gender</span><span class="value">${pd.gender === 'M' ? 'Male' : pd.gender === 'F' ? 'Female' : pd.gender || '-'}</span></div>
            <div class="detail-row"><span class="label">Constitution Type</span><span class="value">${pd.constitution_type || '-'}</span></div>
        </div>

        <!-- Identity Information -->
        <div class="card">
            <h3 class="card-title">Identity Information</h3>
            <div class="detail-row"><span class="label">PAN</span><span class="value">${pd.pan || '-'}</span></div>
            <div class="detail-row"><span class="label">Aadhaar Number</span><span class="value">${getIdentity("Proof Of Aadhaar") || '-'}</span></div>
            <div class="detail-row"><span class="label">CKYC ID</span><span class="value">${pd.ckyc_no || '-'}</span></div>
            <div class="detail-row"><span class="label">CKYC Reference ID</span><span class="value">${pd.ckyc_reference_id || '-'}</span></div>
        </div>

        <!-- Contact Information -->
        <div class="card">
            <h3 class="card-title">Contact Information</h3>
            <div class="detail-row"><span class="label">Mobile Number</span><span class="value">${pd.mob_code ? `+${pd.mob_code} ${pd.mob_num}` : pd.mob_num || '-'}</span></div>
            <div class="detail-row"><span class="label">Email ID</span><span class="value">${pd.email || '-'}</span></div>
            <div class="detail-row"><span class="label">Telephone</span><span class="value">${pd.resi_std_code ? `${pd.resi_std_code}-${pd.resi_tel_num}` : '-'}</span></div>
        </div>

        <!-- Photo -->
        <div class="card photo-card">
            <h3 class="card-title" style="width: 100%; text-align: left;">Photo</h3>
            ${photograph ? `<img src="${photograph}" alt="CKYC Photo" class="photo-img" />` : `<div class="photo-placeholder">No Photo</div>`}
            <span style="font-size: 11px; color: #757575;">Photo available in CKYC</span>
        </div>
    </div>

    <!-- Address Details -->
    <div class="card" style="margin-bottom: 16px;">
        <h3 class="card-title">Address Details</h3>
        <div class="grid" style="margin-bottom: 0;">
            <div>
                <div class="sub-header">Current Address</div>
                <div class="detail-row"><span class="label">House / Flat</span><span class="value">${pd.corres_line1 || '-'}</span></div>
                <div class="detail-row"><span class="label">Street / Area</span><span class="value">${pd.corres_line2 || '-'}</span></div>
                <div class="detail-row"><span class="label">Landmark</span><span class="value">${pd.corres_line3 || '-'}</span></div>
                <div class="detail-row"><span class="label">City / State</span><span class="value">${pd.corres_city || '-'}, ${pd.corres_state || '-'}</span></div>
                <div class="detail-row"><span class="label">Country / PIN</span><span class="value">${pd.corres_country || '-'} - ${pd.corres_pin || '-'}</span></div>
            </div>
            <div>
                <div class="sub-header">Permanent Address</div>
                <div class="detail-row"><span class="label">House / Flat</span><span class="value">${pd.perm_line1 || '-'}</span></div>
                <div class="detail-row"><span class="label">Street / Area</span><span class="value">${pd.perm_line2 || '-'}</span></div>
                <div class="detail-row"><span class="label">Landmark</span><span class="value">${pd.perm_line3 || '-'}</span></div>
                <div class="detail-row"><span class="label">City / State</span><span class="value">${pd.perm_city || '-'}, ${pd.perm_state || '-'}</span></div>
                <div class="detail-row"><span class="label">Country / PIN</span><span class="value">${pd.perm_country || '-'} - ${pd.perm_pin || '-'}</span></div>
            </div>
        </div>
    </div>

    <div class="grid">
        <!-- CKYC Information -->
        <div class="card">
            <h3 class="card-title">CKYC Information</h3>
            <div class="detail-row"><span class="label">Registration Date</span><span class="value">${pd.dec_date || '-'}</span></div>
            <div class="detail-row"><span class="label">Reporting Entity</span><span class="value">Central KYC Records Registry</span></div>
            <div class="detail-row"><span class="label">Declaration Place</span><span class="value">${pd.dec_place || '-'}</span></div>
            <div class="detail-row"><span class="label">KYC Verification Date</span><span class="value">${pd.kyc_date || '-'}</span></div>
        </div>

        <!-- Document Details -->
        <div class="card">
            <h3 class="card-title">Document Details</h3>
            <table>
                <thead>
                    <tr>
                        <th>Document Type</th>
                        <th>Document Number</th>
                    </tr>
                </thead>
                <tbody>
                    ${idDetails.map(doc => `
                        <tr>
                            <td>${doc.ident_type}</td>
                            <td>${doc.ident_num}</td>
                        </tr>
                    `).join('')}
                    ${idDetails.length === 0 ? '<tr><td colspan="2" style="text-align:center;">No documents found</td></tr>' : ''}
                </tbody>
            </table>
        </div>
    </div>

    <!-- Other Information -->
    <div class="card" style="margin-bottom: 16px;">
        <h3 class="card-title">Other Information</h3>
        <div class="detail-row"><span class="label">Source</span><span class="value">Central KYC Records Registry</span></div>
        <div class="detail-row"><span class="label">Remarks</span><span class="value">${pd.remarks || '-'}</span></div>
    </div>

    <div class="footer">
        <span>Confidential - For Official Lending Assessment Only</span>
        <span>AFI Loans & Underwriting Portal</span>
    </div>

    <script>
        window.onload = function() {
            setTimeout(function() {
                window.print();
            }, 300);
        };
    </script>
</body>
</html>`;

    // Trigger Print / PDF window
    const printWindow = window.open('', '_blank');
    if (printWindow) {
        printWindow.document.write(htmlContent);
        printWindow.document.close();
        printWindow.focus();
    }

    // Also download HTML report file
    try {
        const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = `CKYC_Report_${ckycNo}_${pan || ''}.html`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
        console.warn("HTML Blob download error", e);
    }
};
